import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { 
  FaUsers, 
  FaUserClock, 
  FaClock, 
  FaCalendarAlt 
} from 'react-icons/fa';
import { getDailyVisitors as getSearchConsoleVisitors } from '../../services/searchConsoleService';
import './Dashboard.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [dailyVisitors, setDailyVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalStats, setTotalStats] = useState({
    totalClicks: 0,
    totalImpressions: 0,
    avgCtr: 0,
    avgPosition: 0
  });

  const fetchSearchConsoleData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSearchConsoleVisitors(30); // Son 30 günün verilerini al
      setDailyVisitors(data);

      // Toplam istatistikleri hesapla
      const stats = data.reduce((acc, curr) => {
        acc.totalClicks += curr.clicks;
        acc.totalImpressions += curr.impressions;
        acc.avgCtr += curr.ctr;
        acc.avgPosition += curr.position;
        return acc;
      }, { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 });

      // Ortalamaları hesapla
      if (data.length > 0) {
        stats.avgCtr = (stats.avgCtr / data.length).toFixed(2);
        stats.avgPosition = (stats.avgPosition / data.length).toFixed(2);
      }

      setTotalStats(stats);
      setError(null);
    } catch (err) {
      console.error('Search Console verisi alınırken hata:', err);
      setError('Veriler alınırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSearchConsoleData();
  }, [fetchSearchConsoleData]);


  const formatNumber = (num) => {
    return num.toLocaleString('tr-TR');
  };

  const formatPercentage = (num) => {
    return `%${num}`;
  };

  const formatPosition = (num) => {
    return num;
  };

  const chartData = {
    labels: dailyVisitors.map(item => {
      // Tarihi daha okunabilir formata çevir
      const date = new Date(item.date);
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    }),
    datasets: [
      {
        label: 'Gösterim',
        data: dailyVisitors.map(item => item.impressions),
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1
      },
      {
        label: 'Tıklanma',
        data: dailyVisitors.map(item => item.clicks),
        fill: false,
        borderColor: 'rgb(255, 99, 132)',
        tension: 0.1
      },
      {
        label: 'CTR (%)',
        data: dailyVisitors.map(item => item.ctr),
        fill: false,
        borderColor: 'rgb(255, 206, 86)',
        tension: 0.1,
        yAxisID: 'percentage'
      },
      {
        label: 'Sıralama',
        data: dailyVisitors.map(item => item.position),
        fill: false,
        borderColor: 'rgb(153, 102, 255)',
        tension: 0.1,
        yAxisID: 'position'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Search Console Verileri (Son 30 Gün)'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const datasetLabel = context.dataset.label;
            const value = context.parsed.y;
            if (datasetLabel === 'CTR') {
              return `${datasetLabel}: ${value.toFixed(2)}%`;
            }
            return `${datasetLabel}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Gösterim/Tıklanma'
        }
      },
      percentage: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'CTR (%)'
        },
        grid: {
          drawOnChartArea: false
        }
      },
      position: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'Sıralama'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Row>
          <Col>
            <Card>
              <Card.Body className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="mt-3 mb-0">Search Console verileri yükleniyor...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    if (error) {
      return (
        <Row>
          <Col>
            <Card>
              <Card.Body className="text-center p-5">
                <div className="text-danger mb-3">
                  <i className="fas fa-exclamation-circle fa-3x"></i>
                </div>
                <h5 className="text-danger">Veriler Alınamadı</h5>
                <p className="text-muted mb-0">{error}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <Container fluid className="dashboard">
      {/* <div className="d-flex justify-content-between align-items-center mb-4">
        {!loading && !error && (
          <button 
            className="btn btn-outline-primary" 
            onClick={() => fetchSearchConsoleData()}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Yenile
          </button>
        )}
      </div> */}

      {renderContent()}

      {!loading && !error && (
        <>
      
      <Row>
        <Col lg={3} md={6} className="mb-4">
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Toplam Tıklanma</h6>
                  <h3 className="mb-0">{formatNumber(totalStats.totalClicks)}</h3>
                </div>
                <div className="icon-box bg-primary">
                  <FaUsers size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Toplam Gösterim</h6>
                  <h3 className="mb-0">{formatNumber(totalStats.totalImpressions)}</h3>
                </div>
                <div className="icon-box bg-success">
                  <FaUserClock size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Ortalama CTR</h6>
                  <h3 className="mb-0">{formatPercentage(totalStats.avgCtr)}</h3>
                </div>
                <div className="icon-box bg-warning">
                  <FaClock size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted">Ortalama Sıralama</h6>
                  <h3 className="mb-0">{formatPosition(totalStats.avgPosition)}</h3>
                </div>
                <div className="icon-box bg-info">
                  <FaCalendarAlt size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Line data={chartData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
        </>
      )}
    </Container>
  );
};

export default Dashboard;
